import { ILibraryFontItem } from 'store/types/editor'

export interface ITextStyle {
  id: number
  text: string
  font: ILibraryFontItem
  fillColor: string
  strokeColor: string
  strokeThickness: number
  previewPath: string
}

interface ITextStylePreset {
  /** Try in order until a font exists in `libraryFonts` */
  fontPostscriptNames: readonly string[]
  fillColor: string
  strokeColor: string
  strokeThickness: number
}

const findFontInLibrary = (
  libraryFonts: ILibraryFontItem[],
  postscriptNames: readonly string[]
): ILibraryFontItem | undefined => {
  for (const name of postscriptNames) {
    const match = libraryFonts.find(f => f.postscriptName === name)
    if (match) return match
  }
  return undefined
}

/**
 * Each card uses a fixed font (by PostScript name), resolved from the API list.
 * Order here does not depend on `libraryFonts` array order.
 */

const TEXT_STYLE_PRESETS: ITextStylePreset[] = [
  {
    fontPostscriptNames: ['Montserrat-SemiBold'],
    fillColor: '#ffffff',
    strokeColor: '#000000',
    strokeThickness: 4
  },
  {
    fontPostscriptNames: ['Montserrat-SemiBold'],
    fillColor: '#000000',
    strokeColor: '#ffffff',
    strokeThickness: 4
  },
  {
    fontPostscriptNames: ['RacingSansOne-Regular'],
    fillColor: '#70FFAC',
    strokeColor: '#5500FF',
    strokeThickness: 5
  },
  {
    fontPostscriptNames: ['AbrilFatface-Regular'],
    fillColor: '#FFFFAB',
    strokeColor: '#000000',
    strokeThickness: 0
  },
  {
    fontPostscriptNames: ['CaveatBrush-Regular'],
    fillColor: '#FEE255',
    strokeColor: '#000000',
    strokeThickness: 3
  },
  {
    fontPostscriptNames: ['Roboto-Medium'],
    fillColor: '#FFD7FE',
    strokeColor: '#000000',
    strokeThickness: 0
  },
  {
    fontPostscriptNames: ['BalooBhaijaan-Regular'],
    fillColor: '#BC073A',
    strokeColor: '#FFD7FE',
    strokeThickness: 5
  },
  {
    fontPostscriptNames: ['BreeSerif-Regular'],
    fillColor: '#FCCD77',
    strokeColor: '#2C220D',
    strokeThickness: 2
  }
]

const getPreviewPath = (presetIndex: number) =>
  `https://static.rfstat.com/renderforest/images/v2/v-editor/happiness-${presetIndex}.png`

/** Warm HTTP cache for worker `fetch`; deduped per path for this session */
const prefetchedFontPaths = new Set<string>()

const prefetchFontFile = (url: string) => {
  if (!url || prefetchedFontPaths.has(url)) return
  prefetchedFontPaths.add(url)
  void fetch(url)
    .then(res => (res.ok ? res.arrayBuffer() : undefined))
    .catch(() => {})
}

export const buildTextStylesFromLibraryFonts = (
  libraryFonts: ILibraryFontItem[]
): ITextStyle[] => {
  const styles: ITextStyle[] = []
  let nextId = 1

  TEXT_STYLE_PRESETS.forEach((preset, presetIndex) => {
    const font = findFontInLibrary(libraryFonts, preset.fontPostscriptNames)
    if (!font) return

    prefetchFontFile(font.path)

    styles.push({
      id: nextId,
      text: 'Happiness',
      font,
      fillColor: preset.fillColor,
      strokeColor: preset.strokeColor,
      strokeThickness: preset.strokeThickness,
      previewPath: getPreviewPath(presetIndex + 1)
    })
    nextId += 1
  })

  return styles
}
